import { isServer } from '@atlassian/eoc-common';
import { HTTPError, type NetworkError, StreamedHTTPError } from '@atlassian/eoc-global-context';

import { JSM_BASE_URL } from './constants';
import { type EocRequestInit, type JSMError } from './types';

let metaCloudId: string | undefined;
let isJSMAvailable = true;

/**
 * We are getting attributes from meta in the generated HTML by server-side.
 * If __SERVER__ is available, we will get meta attributes from ssr__metadata
 * because Jira puts meta attributes that come from BE into this variable.
 * @param name {string}
 * @returns {string | undefined}
 */
export function getMeta(name: string): string | undefined {
	const globalVariable = window ?? global;

	if (isServer() && (globalVariable as any).ssr__metadata) {
		return (globalVariable as any).ssr__metadata[name];
	}

	if (metaCloudId) {
		return metaCloudId;
	}

	if (isJSMAvailable) {
		const element = document.querySelector(`meta[name="${name}"]`);

		metaCloudId = element?.getAttribute('content') ?? undefined;

		isJSMAvailable = !!metaCloudId;
	}

	return metaCloudId;
}

/**
 * Exported to set cloudId in the test
 */
export function setMetaCloudId(id: string | undefined) {
	metaCloudId = id;
}

export function withPrefix(input: string, init?: EocRequestInit): string {
	if (init?.exactUrl) {
		return input;
	}

	// If current env is not opsgenie, we will add cloud id to base url of request.
	if ((window as any).__OG__) {
		return '/webapi' + input;
	}

	const cloudId = getMeta('ajs-cloud-id');
	if (cloudId) {
		return JSM_BASE_URL + cloudId + input;
	}

	return input;
}

// https://www.rfc-editor.org/rfc/rfc9110.html#section-8.3
// This is not a full implementation, but should skip any unknown parameters
// and match the charset if it exists. In case there are multiple (which shouldn't) we still
// match the first occurence. If only application/json is returned, utf-8 is
// assumed as we will try to parse it as utf-8 json. This is probably our best
// guess without too much effort here.
const mediaTypeRegex =
	/(^application\/json$)|(^application\/json([\s\t]?;.+?)*([\s\t]?;[\s\t]?charset=(("utf-8")|(utf-8))))/i;

/**
 * This is the default transformer that we apply to error streams.
 * It will stream the body and convert it into a StreamedHTTPError that has its
 * message replaced with the one from the backend.
 *
 * This means that we always resolve the error in fetch and do not immediately
 * show the error to the user even if we know it is an error before receiving the
 * full message. Still, we can change the defaultCatch/ErrorBoundary to wait for
 * the response and update the UI later when a raw HTTPError is thrown in the
 * future.
 *
 * @param e base HTTPError to process
 */
export const defaultErrorTransformer = (e: NetworkError) => {
	if (e instanceof HTTPError) {
		const contentType = e.response.headers.get('Content-Type');
		// The content type is application/json, and it is highly likely utf-8 encoded
		if (contentType && mediaTypeRegex.test(contentType)) {
			return e.response
				.json()
				.then((data) => new StreamedHTTPError(e, data, data.errorMessage ?? data.message));
		}
		// For anything else, try to interpret it as utf8 text as best effort.
		return e.response.text().then((message) => new StreamedHTTPError(e, message, message));
	}
	return e;
};

/**
 * Since JSM endpoint returns different response according to our response,
 * this function can help to parse error messages.
 * @param error base HTTPError to process
 */
export const jsmErrorTransformer = (error: NetworkError) => {
	if (error instanceof HTTPError) {
		const contentType = error.response.headers.get('Content-Type');

		if (contentType && mediaTypeRegex.test(contentType)) {
			return error.response
				.json()
				.then(
					(data: JSMError) =>
						new StreamedHTTPError<JSMError>(error, data, data.errorMessages.join(',')),
				);
		}

		return error.response
			.text()
			.then((message) => new StreamedHTTPError<string>(error, message, message));
	}

	return error;
};

export const getRequestUrl = (fetchInput: RequestInfo | URL): string =>
	typeof fetchInput === 'string'
		? fetchInput
		: fetchInput instanceof URL
			? fetchInput.href
			: fetchInput.url;

/**
 * @param url The URL you want to sanitize
 * @returns A URL string without any user generated content (UGC) or PII (personally identifiable information). The URL will not necessarily be valid URL.
 */
export const sanitizeUrlString = (url: string) => {
	const urlWithoutQueryParams = url.replace(/\?.*$/, '');
	const urlWithoutUUIDs = urlWithoutQueryParams.replace(
		/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(-[0-9a-f]{13})?/gi,
		'<uuid>',
	);

	return urlWithoutUUIDs;
};
